import { Box, Button, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProfileInteractionAttributes } from '@loyalty/shared-common/profile-api-client';
import { colorPalette, sxs } from '@qualifio/ui-commons';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons/faCalendar';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProfileInteractionDetailDialog } from './ProfileInteractionDetailDialog';
import { QlfDate } from '../../../shared/qlf-date/QlfDate';
import { SxStyles } from '../../../shared/utils/types';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useProfiles } from '../../ProfileContext';
import { useProgram } from '../../../loyalty/ProgramContext';

const pageSize = 10;

const styles: SxStyles = {
  headerCell: {
    fontSize: '14px',
    padding: 1,
    textAlign: 'left',
    '&:first-of-type': {
      borderTopLeftRadius: 5,
    },
    '&:last-of-type': {
      borderTopRightRadius: 5,
    },
  },
  headerIcon: {
    marginRight: 0.5,
  },
  cell: {
    padding: 1,
    borderTop: '1px solid #EAEAEA',
    borderBottom: '1px solid #EAEAEA',
  },
};

export function ProfileInteractions() {
  const { t } = useTranslation();
  const { selectedProfile } = useProfiles();
  const { programId } = useProgram();
  const { profileApi } = useApi();
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedInteraction, setSelectedInteraction] =
    useState<ProfileInteractionAttributes | null>(null);
  const [interactionsData, setInteractionsData] = useState({
    data: [] as ProfileInteractionAttributes[],
    isLast: true,
    pageCount: 0,
  });

  if (!selectedProfile) {
    throw new Error('No profile selected');
  }

  const fetchInteractions = async (limit: number, skip: number) => {
    const interactionsInfos = await profileApi.getProfileInteractions(selectedProfile.id, {
      programId,
      limit,
      skip,
    });
    setInteractionsData(interactionsInfos);
  };

  useEffect(() => {
    fetchInteractions(pageSize, 0);
    setCurrentPage(0);
    setSelectedInteraction(null);
  }, [selectedProfile]);

  const nextPage = async () => {
    await fetchInteractions(pageSize, (currentPage + 1) * pageSize);
    setCurrentPage(prev => prev + 1);
  };

  const previousPage = async () => {
    await fetchInteractions(pageSize, (currentPage - 1) * pageSize);
    setCurrentPage(prev => prev - 1);
  };

  if (interactionsData?.data?.length === 0) {
    return (
      <Box component="p" sx={{ color: colorPalette.grey.mainHover }}>
        {t('profiles.details.interactions.no-interaction')}
      </Box>
    );
  }

  return (
    <div>
      <Box component="table" sx={{ width: '100%', borderSpacing: 0 }}>
        <Box
          component="thead"
          sx={{
            backgroundColor: colorPalette.marine.main,
            color: colorPalette.marine.contrastText,
            fontWeight: 'bold',
            fontSize: '1.15em',
          }}
        >
          <tr>
            <Box component="th" sx={sxs(styles.headerCell, { textAlign: 'left', width: '20%' })}>
              <Box
                component={FontAwesomeIcon}
                icon={faCalendar}
                sx={styles.headerIcon}
                fixedWidth
              />
              {t('profiles.details.interactions.list.date')}
            </Box>
            <Box component="th" sx={sxs(styles.headerCell, { textAlign: 'left', width: '45%' })}>
              {t('profiles.details.interactions.list.label')}
            </Box>
            <Box component="th" sx={sxs(styles.headerCell, { textAlign: 'left', width: '25%' })}>
              {t('profiles.details.interactions.list.status')}
            </Box>
            <Box
              component="th"
              sx={sxs(styles.headerCell, { textAlign: 'center', width: '10%' })}
              aria-label={t('profiles.details.interactions.list.detail')}
            />
          </tr>
        </Box>
        <tbody>
          {interactionsData.data.map(interaction => (
            <Box
              component="tr"
              key={interaction.id}
              sx={{ background: ({ palette }) => palette.background.paper }}
            >
              <Box
                component="td"
                sx={sxs(styles.cell, {
                  color: colorPalette.grey.dark,
                })}
              >
                <QlfDate date={interaction.eventTimestamp} />
              </Box>
              <Box
                component="td"
                sx={sxs(styles.cell, {
                  fontFamily: 'Open Sans, sans-serif',
                  fontSize: '14px',
                  color: colorPalette.grey.dark,
                  minWidth: 'fit-content',
                })}
              >
                {interaction.label}
              </Box>
              <Box
                component="td"
                sx={sxs(styles.cell, {
                  fontFamily: 'Open Sans, sans-serif',
                  fontSize: '14px',
                  color: colorPalette.grey.dark,
                  minWidth: 'fit-content',
                })}
              >
                {interaction.status}
              </Box>
              <Box
                component="td"
                sx={sxs(styles.cell, {
                  textAlign: 'center',
                })}
              >
                <IconButton
                  size="small"
                  aria-label={t('profiles.details.interactions.detail.open')}
                  aria-haspopup="dialog"
                  onClick={() => setSelectedInteraction(interaction)}
                  sx={{ color: colorPalette.marine.main }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </tbody>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
          alignItems: 'center',
        }}
      >
        <Button color="grey" disabled={currentPage === 0} onClick={previousPage}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <Box
          sx={{
            fontSize: 16,
            marginX: 1,
          }}
        >{`${currentPage + 1} / ${
          interactionsData.pageCount == 0 ? 1 : interactionsData.pageCount
        }`}</Box>
        <Button color="grey" disabled={interactionsData.isLast} onClick={nextPage}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </Box>

      {selectedInteraction ? (
        <ProfileInteractionDetailDialog
          profileId={selectedProfile.id}
          profileInteractionId={selectedInteraction.id}
          title={selectedInteraction.label}
          onClose={() => setSelectedInteraction(null)}
        />
      ) : null}
    </div>
  );
}
